import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PostContext } from "../../contextStore/PostContext";
import client from "../../api/client";
import "./View.css";

// Components
import BuyerPlansModal from "../../Components/Plans/BuyerPlansModal";
import ContactButton from "../../Components/ContactButton/ContactButton";
import Footer from "../../Components/Footer/Footer";
import Header from "../../Components/Header/Header";
import { AuthContext } from "../../contextStore/AuthContext";

// --- normalized API base (prefers axios client.baseURL, then env, otherwise same-origin) ---
const RAW_CLIENT_BASE =
  client && client.defaults && client.defaults.baseURL
    ? String(client.defaults.baseURL).trim()
    : "";

const ENV_BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_URL || "";

// normalize function: remove trailing slashes and trailing '/api' so we don't end up with /api/api/...
function normalizeBaseUrl(raw) {
  if (!raw) return "";
  let v = String(raw).trim();
  v = v.replace(/\/+$/, "");    // remove trailing slashes
  v = v.replace(/\/api$/i, ""); // strip trailing '/api'
  return v;
}

const API_BASE =
  normalizeBaseUrl(RAW_CLIENT_BASE) ||
  normalizeBaseUrl(ENV_BASE) ||
  ""; // empty = same-origin

const PLACEHOLDER_IMG = API_BASE ? `${API_BASE}/placeholder-machine.jpg` : "/placeholder-machine.jpg";


import {
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaIndustry,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaShieldAlt,
  FaCheckCircle,
  FaEye,
} from "react-icons/fa";



function View() {
  const { postContent } = useContext(PostContext);
  const { token } = useContext(AuthContext) || {};
  const { id: paramId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [machine, setMachine] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showPlans, setShowPlans] = useState(false);
  const [plansFromServer, setPlansFromServer] = useState(null);
  const [viewRecording, setViewRecording] = useState(false);

  const routeId =
    paramId ||
    (location.state && (location.state.id || location.state._id)) ||
    (postContent && postContent.id);

  // Helper: safely merge server machine into existing state while preserving views
  const mergeServerMachine = (serverMachine) => {
    setMachine((prev) => {
      const serverViews = (serverMachine && typeof serverMachine.views === "number") ? serverMachine.views : undefined;
      return {
        ...prev,
        ...serverMachine,
        views: (typeof serverViews === "number") ? serverViews : (prev?.views ?? (typeof serverMachine?.views === 'number' ? serverMachine.views : 0)),
      };
    });
  };

  // Record view helper
  const recordView = async (id) => {
    if (!id) return;
    try {
      const key = `viewed_machine_${id}`;
      const last = window.localStorage.getItem(key);
      const windowMs = 30 * 60 * 1000; // 30 minutes
      if (last && (Date.now() - Number(last) < windowMs)) return;

      setViewRecording(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const vr = await client.post(`/api/machines/${id}/view`, {}, { headers });

      if (vr && vr.data && vr.data.ok && vr.data.machine) {
        // merge server machine safely
        mergeServerMachine(vr.data.machine);
      } else {
        // optimistic increment fallback
        setMachine(prev => prev ? { ...prev, views: (Number(prev.views) || 0) + 1 } : prev);
      }

      // mark viewed locally to avoid repeat within the debounce window
      window.localStorage.setItem(key, String(Date.now()));
    } catch (err) {
      console.warn("record view failed", err);
      // optimistic fallback if any error
      setMachine(prev => prev ? { ...prev, views: (Number(prev.views) || 0) + 1 } : prev);
    } finally {
      setViewRecording(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");

      if (!routeId) {
        setLoading(false);
        setError("No product selected. Please open from the product list.");
        return;
      }

      try {
        const res = await client.get(`/api/machines/${routeId}`);
        if (!res?.data) throw new Error("Empty response from server");

        if (res.data.ok && res.data.machine) {
          const mRaw = res.data.machine;
          const m = { ...mRaw };

          // --- Defensive normalization ---

          // Parse industries if stored as string
          try {
            if (m.industries && typeof m.industries === "string") {
              const parsed = JSON.parse(m.industries);
              m.industries = Array.isArray(parsed) ? parsed : [String(parsed)];
            }
          } catch {
            if (m.industries && !Array.isArray(m.industries)) {
              m.industries = [m.industries];
            } else {
              m.industries = m.industries || [];
            }
          }

          // Normalize common names front-end expects
          m.manufacturer = m.manufacturer || m.make || null;
          m.model_year = m.model_year || m.manu_year || null;
          m.model = m.model || m.model_number || null;

          // Ensure make & production fields exist
          m.make =
            m.make ||
            m.manufacturer ||
            m.manufactured_by ||
            m.manufacturing_by ||
            null;

          m.production =
            m.production ||
            m.production_capacity ||
            m.production_rate ||
            m.prod ||
            null;

          if (typeof m.make === "string") m.make = m.make.trim() || null;
          if (typeof m.production === "string")
            m.production = m.production.trim() || null;

          // Normalize condition
          m.condition =
            m.condition ||
            m.machine_condition ||
            (m.is_new ? "New" : "Used") ||
            "Used";

          // Ensure images array
          if (!Array.isArray(m.images)) {
            if (m.url) m.images = [{ url: m.url }];
            else m.images = [];
          }

          // Parse specification if JSON string
          try {
            if (m.specification && typeof m.specification === "string") {
              m.specification = JSON.parse(m.specification);
            }
          } catch { }

          // Category resolution
          const INDUSTRY_MAP = {
            1: "Textile Industries",
            2: "Food Industries",
            3: "Pharma Industries",
            4: "Construction",
            5: "Primary Packaging",
            6: "Special Purpose Machines",
            7: "Cosmetic",
            8: "Secondary Packaging",
            9: "Stationary",
            10: "Parts Counting Machinery",
            11: "Parts Assembly Machine",
            12: "Gems and Jewellery",
            13: "Non Woven Product Machinery",
            14: "Others",
          };

          const industryNameFromRow =
            m.industry_name || m.industry || m.category || null;
          let industryNameResolved = industryNameFromRow;

          if (!industryNameResolved && Array.isArray(m.industries) && m.industries.length) {
            industryNameResolved = m.industries[0];
          }

          if (!industryNameResolved && m.industry_id != null) {
            const idNum = Number(m.industry_id);
            industryNameResolved = INDUSTRY_MAP[idNum] || `Category ${m.industry_id}`;
          }

          m.industry_name = industryNameResolved;
          if (!Array.isArray(m.industries)) m.industries = [];
          if (m.industry_name && m.industries.length === 0)
            m.industries = [m.industry_name];
          if (m.industry_name && m.industries[0] !== m.industry_name)
            m.industries.unshift(m.industry_name);

          // Ensure city fields
          m.location_city = m.location_city || m.city || null;

          if (!cancelled) {
            // use safe merge so we don't accidentally wipe views if server omits it
            setMachine(prev => {
              const serverViews = (typeof m.views === "number") ? m.views : undefined;
              return {
                ...prev,
                ...m,
                views: (typeof serverViews === "number") ? serverViews : (prev?.views ?? (typeof m.views === 'number' ? m.views : 0)),
              };
            });

            setSeller({
              id: m.seller_id,
              name: m.seller_name || m.seller || "Seller",
              email: m.seller_email || null,
              phone: m.seller_phone || null,
              verified: m.seller_verified || false,
            });

            // record the view (debounced by localStorage inside recordView)
            recordView(routeId).catch(e => console.warn('recordView error', e));
          }
        } else {
          const errMsg =
            res.data?.error || res.data?.message || "Machine not found";
          throw new Error(errMsg);
        }
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.response?.data?.error ||
            err?.message ||
            "Failed to load machine";
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [routeId, paramId, location.state, postContent, token]);

  const formatPrice = (price) => new Intl.NumberFormat("en-IN").format(price);

  if (loading)
    return (
      <div className="view-page-container">
        <Header />
        <div className="view-loading">
          <div className="loading-spinner"></div>
          <p>Loading product details...</p>
        </div>
        <Footer />
      </div>
    );

  if (error)
    return (
      <div className="view-page-container">
        <Header />
        <div className="view-error">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Product</h3>
          <p>{error}</p>
          <button className="back-button" onClick={() => navigate("/")}>
            Back to Home
          </button>
        </div>
        <Footer />
      </div>
    );

  if (!machine)
    return (
      <div className="view-page-container">
        <Header />
        <div className="view-error">
          <h3>Product Not Found</h3>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <button className="back-button" onClick={() => navigate("/")}>
            Browse Other Machines
          </button>
        </div>
        <Footer />
      </div>
    );

  const INDUSTRY_MAP = {
    1: "Textile Industries",
    2: "Food Industries",
    3: "Pharma Industries",
    4: "Construction",
    5: "Primary Packaging",
    6: "Special Purpose Machines",
    7: "Cosmetic",
    8: "Secondary Packaging",
    9: "Stationary",
    10: "Parts Counting Machinery",
    11: "Parts Assembly Machine",
    12: "Gems and Jewellery",
    13: "Non Woven Product Machinery",
    14: "Others",
  };

  const categoryLabel =
    machine.industry_name ||
    (Array.isArray(machine.industries)
      ? machine.industries[0]
      : machine.industries) ||
    machine.industry ||
    machine.category ||
    (machine.industry_id
      ? INDUSTRY_MAP[Number(machine.industry_id)] ||
      `Category ${machine.industry_id}`
      : "—");

  const images =
    machine.images && machine.images.length
      ? machine.images
      : machine.url
        ? [{ url: machine.url }]
        : [];

  const rawLabel =
    machine.location_label || machine.location_city || machine.city || "";
  const lat =
    machine.location_lat != null ? parseFloat(machine.location_lat) : NaN;
  const lng =
    machine.location_lng != null ? parseFloat(machine.location_lng) : NaN;

  const hasValidCoords = !Number.isNaN(lat) && !Number.isNaN(lng);

  const mapUrl = hasValidCoords
    ? `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`
    : `https://maps.google.com/maps?q=${encodeURIComponent(
      rawLabel || "India"
    )}&z=6&output=embed`;

  const displayLocationName =
    rawLabel ||
    (hasValidCoords
      ? `Lat ${lat.toFixed(4)}, Lng ${lng.toFixed(4)}`
      : "Unknown location");

  return (
    <div className="view-page-container">
      <Header />

      <div className="view-content">
        <div className="breadcrumb">
          <span onClick={() => navigate("/")}>Home</span>
          <span> › </span>
          <span onClick={() => navigate("/category/all")}>Machinery</span>
          <span> › </span>
          <span className="current">{machine.title || machine.name}</span>
        </div>

        <div className="view-layout">
          {/* Left Column */}
          <div className="left-column">
            <div className="image-gallery">
              <div className="main-image">
                <img
                  src={images[activeImageIndex]?.url || PLACEHOLDER_IMG}
                  alt={machine.title || machine.name}
                  onError={(e) => (e.target.src = PLACEHOLDER_IMG)}
                />
              </div>

              {images.length > 1 && (
                <div className="image-thumbnails">
                  {images.map((img, index) => (
                    <div
                      key={index}
                      className={`thumbnail ${index === activeImageIndex ? "active" : ""
                        }`}
                      onClick={() => setActiveImageIndex(index)}
                    >
                      <img
                        src={img.url}
                        alt={`${machine.title || "Machine"} ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ✅ Product Details Card */}
            <div className="details-card">
              <h3>Product Details</h3>

              <div className="detail-item">
                <strong>Category:</strong>
                <span>{categoryLabel}</span>
              </div>

              <div className="detail-item">
                <strong>Condition:</strong>
                <span>{machine.condition || "Used"}</span>
              </div>

              <div className="detail-item">
                <strong>Model Number:</strong>
                <span>{machine.model || "Not specified"}</span>
              </div>

              <div className="detail-item">
                <strong>Model Year:</strong>
                <span>{machine.manu_year || machine.model_year || "Not specified"}</span>
              </div>

              <div className="detail-item">
                <strong>Manufactured By:</strong>
                <span>{machine.make || "Not specified"}</span>
              </div>

              <div className="detail-item">
                <strong>Production:</strong>
                <span>{machine.production || "Not specified"}</span>
              </div>
            </div>

            {/* Description */}
            <div className="description-section">
              <h3>Description</h3>
              <p>
                {machine.description ||
                  (machine.specification && machine.specification.text) ||
                  "No description provided."}
              </p>
            </div>

            {/* Location */}
            <div className="location-section">
              <h3>
                <FaMapMarkerAlt className="icon" />
                Location
              </h3>
              <div className="map-container">
                <iframe
                  src={mapUrl}
                  width="100%"
                  height="300"
                  style={{ border: 0, borderRadius: "8px" }}
                  allowFullScreen=""
                  loading="lazy"
                  title={`Location of ${machine.title || "machine"}`}
                ></iframe>
                <div className="location-info">
                  <FaMapMarkerAlt className="pin-icon" />
                  <span>{displayLocationName}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="right-column">
            <div className="price-card">
              <div className="price">₹ {formatPrice(machine.price)}</div>
              <div className="price-note">Negotiable</div>

              <div className="meta-info">
                <div className="meta-item">
                  <FaCalendarAlt className="icon" />
                  <span>
                    {machine.created_at
                      ? new Date(machine.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })
                      : "Recently posted"}
                  </span>
                </div>
                <div className="meta-item">
                  <FaIndustry className="icon" />
                  <span>{categoryLabel}</span>
                </div>
                <div className="meta-item">
                  <FaMapMarkerAlt className="icon" />
                  <span>{displayLocationName}</span>
                </div>

                {/* Views */}
                <div className="meta-item">
                  <FaEye className="icon" />
                  <span>{machine.views ?? 0} views {viewRecording ? "(saving...)" : ""}</span>
                </div>
              </div>
            </div>

            {/* Seller Card */}
            <div className="seller-card">
              <div className="seller-header">
                <FaUser className="seller-icon" />
                <div className="seller-info">
                  <h4>{seller?.name}</h4>
                  {seller?.verified && (
                    <div className="verified-badge">
                      <FaCheckCircle className="verified-icon" />
                      <span>Verified Seller</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="seller-contact">
                {seller?.phone ? (
                  <>
                    <div className="contact-item">
                      <FaPhone className="contact-icon" />
                      <a href={`tel:${seller.phone}`} className="contact-link">
                        {seller.phone}
                      </a>
                    </div>
                    {seller?.email && (
                      <div className="contact-item">
                        <FaEnvelope className="contact-icon" />
                        <a href={`mailto:${seller.email}`} className="contact-link">
                          {seller.email}
                        </a>
                      </div>
                    )}
                    <button className="whatsapp-button">
                      💬 Chat on WhatsApp
                    </button>
                  </>
                ) : (
                  <ContactButton
                    machineId={routeId}
                    redirectToOnLogin={`/machines/${routeId}`}
                    onContact={(contact) => {
                      setSeller((prev) => ({
                        ...prev,
                        phone: contact.phone,
                        email: contact.email,
                      }));
                      setShowPlans(false);
                    }}
                    onRequirePlans={(plans) => {
                      setPlansFromServer(plans || null);
                      setShowPlans(true);
                    }}
                  >
                    <FaPhone className="button-icon" />
                    Show Contact Details
                  </ContactButton>
                )}
              </div>

              <div className="safety-tips">
                <FaShieldAlt className="safety-icon" />
                <span>
                  ⚠️ Safety Tip

                  Be cautious before making any online payments.
                  Do not transfer money or share sensitive information until you have verified the seller and the product in person.
                  Our platform does not guarantee transactions or ask for payment outside the chat.
                </span>
              </div>
            </div>

            <div className="report-section">
              <button className="report-button">🚩 Report this ad</button>
            </div>
          </div>
        </div>
      </div>

      {showPlans && (
        <BuyerPlansModal
          open={showPlans}
          onClose={() => setShowPlans(false)}
          initialPlans={plansFromServer}
          listingId={routeId}
          onPurchased={async ({ type, planId, paymentId }) => {
            try {
              await new Promise((r) => setTimeout(r, 800));
              const contactRes = await client.get(
                `/api/machines/${routeId}/contact`
              );
              if (
                contactRes.data &&
                contactRes.data.ok &&
                (contactRes.data.phone || contactRes.data.email)
              ) {
                setSeller((prev) => ({
                  ...prev,
                  phone: contactRes.data.phone,
                  email: contactRes.data.email,
                }));
                setShowPlans(false);
              } else {
                alert(
                  "Purchase succeeded. If contact is not visible immediately, please try again in a few seconds."
                );
              }
            } catch (err) {
              console.error("Failed to refresh contact after plan purchase", err);
              alert(
                "Purchase succeeded but could not fetch contact. Please refresh or check after a moment."
              );
            }
          }}
        />
      )}

      <Footer />
    </div>
  );
}

export default View;
